import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { catchError, map, Observable, tap } from 'rxjs'
import { ServicePlanPayload } from 'src/app/models/service-plan/service-plan-payload'
import { ApplicationInsightsService } from '../application-insights/application-insights.service'
import { ConfigService } from '../config/config.service'

export interface ServicePlanPreviewResult {
  pdfBlob: Blob
  templateVersion: number | null
}

// Story e9-1 (AC3/AC4) — calls the already-built POST api/ServicePlan/preview endpoint (ARCH10,
// e6-1) against the current in-memory payload. Deliberately does not interpret the response beyond
// extracting the blob/header — a template-version-mismatch 400 is still a Blob-typed error body
// here (responseType: 'blob' applies to error responses too), and this service re-throws it
// unexamined, matching the house rule "catchError in services re-throws — do not swallow errors
// in services". The caller (ReviewSendStepComponent) is responsible for reading that error body to
// decide between the mismatch banner and a generic failure banner.
@Injectable({
  providedIn: 'root',
})
export class ServicePlanPreviewService {
  private readonly previewUrl =
    this.config.getSettings('webApi').baseUri + 'serviceplan/preview'

  constructor(
    private http: HttpClient,
    private appInsights: ApplicationInsightsService,
    private config: ConfigService,
  ) {}

  GeneratePreview(
    clientId: string,
    schemaVersion: number,
    payload: ServicePlanPayload,
    templateVersion: number | null,
  ): Observable<ServicePlanPreviewResult> {
    const body: Record<string, unknown> = { clientId, schemaVersion, payload }
    if (templateVersion !== null) {
      body['templateVersion'] = templateVersion
    }

    return this.http
      .post(this.previewUrl, body, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response) => ({
          pdfBlob: response.body as Blob,
          templateVersion: this.parseTemplateVersion(
            response.headers.get('X-Template-Version'),
          ),
        })),
        tap((_) =>
          this.appInsights.logEvent('Generated Service Plan PDF preview'),
        ),
        catchError(async (err: HttpErrorResponse) => {
          throw err
        }),
      )
  }

  private parseTemplateVersion(headerValue: string | null): number | null {
    if (!headerValue) {
      return null
    }
    const parsed = Number(headerValue)
    return Number.isNaN(parsed) ? null : parsed
  }
}
